import React, { useCallback, useEffect, useId, useRef, useState } from 'react';
import type { User } from '@supabase/supabase-js';
import { ChevronUp, CreditCard, LogOut, User2 } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import type { ProfileRow } from '../../lib/auth/AuthContext';
import { useAuth } from '../../lib/auth/AuthContext';

function resolveAvatarUrl(user: User | null, profile: ProfileRow | null): string | null {
  const fromProfile = profile?.avatar_url?.trim();
  if (fromProfile) return fromProfile;
  const meta = user?.user_metadata;
  if (!meta || typeof meta !== 'object') return null;
  const m = meta as Record<string, unknown>;
  for (const key of ['avatar_url', 'picture', 'avatarUrl']) {
    const v = m[key];
    if (typeof v === 'string' && v.trim()) return v.trim();
  }
  return null;
}

function formatPlanLine(profile: ProfileRow | null, loading: boolean): string {
  if (loading) return 'Loading plan…';
  if (!profile) return '—';
  const tier = profile.subscription_tier ?? 'free';
  const tierLabel =
    tier === 'premium' ? 'Premium' : tier === 'b2b' ? 'Business' : 'Free';
  const status = profile.subscription_active ? 'Active' : 'Not subscribed';
  return `${tierLabel} · ${status}`;
}

type AccountMenuProps = {
  /** Wide sidebar row vs compact header icon */
  variant?: 'sidebar' | 'header';
};

export const AccountMenu: React.FC<AccountMenuProps> = ({ variant = 'sidebar' }) => {
  const menuId = useId();
  const navigate = useNavigate();
  const { user, profile, profileLoading, signOut } = useAuth();
  const [open, setOpen] = useState(false);
  const rootRef = useRef<HTMLDivElement>(null);

  const avatarUrl = resolveAvatarUrl(user, profile);
  const email = profile?.email ?? user?.email ?? '—';
  const planLine = formatPlanLine(profile, profileLoading);

  const close = useCallback(() => setOpen(false), []);

  useEffect(() => {
    if (!open) return;
    const onDoc = (e: MouseEvent) => {
      if (!rootRef.current?.contains(e.target as Node)) close();
    };
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') close();
    };
    document.addEventListener('mousedown', onDoc);
    window.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('mousedown', onDoc);
      window.removeEventListener('keydown', onKey);
    };
  }, [open, close]);

  const goBilling = () => {
    close();
    navigate('/app/billing');
  };

  const handleLogout = async () => {
    close();
    await signOut();
    navigate('/auth', { replace: true });
  };

  const isHeader = variant === 'header';

  return (
    <div ref={rootRef} className={`relative ${isHeader ? 'flex justify-end' : ''}`}>
      <button
        type="button"
        aria-expanded={open}
        aria-haspopup="menu"
        aria-controls={open ? menuId : undefined}
        onClick={() => setOpen((v) => !v)}
        className={[
          'group flex items-center transition-all duration-200 ease-soft-spring focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brandLilac/50',
          isHeader
            ? 'h-10 w-10 shrink-0 justify-center overflow-hidden rounded-xl border border-borderSoft/35 bg-white/90 p-0 shadow-sm hover:bg-white hover:shadow-[0_6px_24px_-12px_rgba(59,51,133,0.08)]'
            : 'w-full gap-3 rounded-2xl px-3 py-2.5 text-left text-textMuted hover:bg-white/85 hover:text-brandDeep hover:shadow-[0_6px_24px_-12px_rgba(59,51,133,0.08)]'
        ].join(' ')}
      >
        <span
          className={[
            'relative inline-flex shrink-0 overflow-hidden text-brandMuted transition-all duration-200 group-hover:text-brandTeal',
            isHeader
              ? 'h-full w-full rounded-xl'
              : 'h-10 w-10 rounded-xl border border-borderSoft/35 bg-white/90 shadow-sm group-hover:border-brandSage/35'
          ].join(' ')}
        >
          {avatarUrl ? (
            <img src={avatarUrl} alt="" className="h-full w-full object-cover" referrerPolicy="no-referrer" />
          ) : (
            <span className="flex h-full w-full items-center justify-center bg-white/90" aria-hidden="true">
              <User2 className="h-[1.125rem] w-[1.125rem]" strokeWidth={2} />
            </span>
          )}
        </span>
        {!isHeader ? (
          <>
            <span className="min-w-0 flex-1">
              <span className="block truncate text-sm font-medium leading-snug text-text group-hover:text-brandDeep">
                {profileLoading ? 'Loading…' : email}
              </span>
              <span className="mt-0.5 block truncate text-xs leading-snug text-textMuted">{planLine}</span>
            </span>
            <ChevronUp
              className={[
                'h-4 w-4 shrink-0 text-textMuted transition-transform duration-200',
                open ? 'rotate-0' : 'rotate-180'
              ].join(' ')}
              aria-hidden
              strokeWidth={2}
            />
          </>
        ) : null}
      </button>

      {open ? (
        <div
          id={menuId}
          role="menu"
          aria-label="Account"
          className={[
            'absolute z-[60] overflow-hidden rounded-2xl border border-borderSoft/50 bg-white/95 py-1.5 shadow-[0_12px_48px_-12px_rgba(59,51,133,0.18)] backdrop-blur-sm',
            isHeader
              ? 'right-0 top-full mt-2 w-[min(18rem,calc(100vw-2rem))]'
              : 'bottom-full left-0 right-0 mb-2'
          ].join(' ')}
        >
          {isHeader ? (
            <div className="border-b border-borderSoft/40 px-3 py-2.5">
              <p className="truncate text-sm font-medium text-text">{profileLoading ? 'Loading…' : email}</p>
              <p className="mt-0.5 truncate text-xs text-textMuted">{planLine}</p>
            </div>
          ) : null}
          <button
            type="button"
            role="menuitem"
            onClick={() => void goBilling()}
            className="flex w-full items-center gap-3 px-3 py-2.5 text-left text-sm font-medium text-textMuted transition-colors hover:bg-brandCream/40 hover:text-brandDeep"
          >
            <span
              className="inline-flex h-9 w-9 shrink-0 items-center justify-center rounded-xl border border-borderSoft/35 bg-white/90 text-brandMuted"
              aria-hidden
            >
              <CreditCard className="h-4 w-4" strokeWidth={2} />
            </span>
            Billing
          </button>
          <button
            type="button"
            role="menuitem"
            onClick={() => void handleLogout()}
            className="flex w-full items-center gap-3 px-3 py-2.5 text-left text-sm font-medium text-textMuted transition-colors hover:bg-brandCream/40 hover:text-brandDeep"
          >
            <span
              className="inline-flex h-9 w-9 shrink-0 items-center justify-center rounded-xl border border-borderSoft/35 bg-white/90 text-brandMuted"
              aria-hidden
            >
              <LogOut className="h-4 w-4" strokeWidth={2} />
            </span>
            Log out
          </button>
        </div>
      ) : null}
    </div>
  );
};
